<!--
  Lead-quality criteria, thresholds and health bands (RF-34, AC-18).
  Hyper card + table + form controls. No Material beyond what the shell already uses.

  Routed page under the CRM group (`/kanban/lead-quality`), so the title and the
  description live in the `page-heading` — repeating them inside the card would
  print the same two lines twice.
-->
<page-heading
  [title]="'leadQuality.config.title' | translate"
  [description]="'leadQuality.config.subtitle' | translate"
/>

<div class="row">
  <div class="col-12">
    <div class="card">
      <div class="card-body">
        @if (!loading) {
          <div class="d-flex justify-content-end mb-2">
            <button class="btn btn-primary" (click)="save()" [disabled]="saving">
              @if (saving) {
                <span class="spinner-border spinner-border-sm me-1" role="status"></span>
              }
              {{ 'leadQuality.config.save' | translate }}
            </button>
          </div>
        }

        @if (loading) {
          <div class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">{{ 'leadQuality.config.loading' | translate }}</span>
            </div>
          </div>
        } @else if (config) {
          <!--
            AC-18 requires the screen to make clear that an unconfigured company is
            following the platform default, rather than showing the values as if the
            company had chosen them.
          -->
          @if (isDefault) {
            <div class="alert alert-info d-flex align-items-start" role="alert">
              <i class="ri-information-line me-2 font-18"></i>
              <div>
                <strong>{{ 'leadQuality.config.usingDefaultTitle' | translate }}</strong>
                <div class="font-13">{{ 'leadQuality.config.usingDefaultHint' | translate }}</div>
              </div>
            </div>
          }

          <!-- Score criteria -->
          <h4 class="header-title mt-3">{{ 'leadQuality.config.scoreCriteria' | translate }}</h4>
          <p class="text-muted font-13">
            {{ weightHint | translate: { total: totalWeight } }}
          </p>

          <div class="table-responsive">
            <table class="table table-centered table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th>{{ 'leadQuality.config.criterion' | translate }}</th>
                  <th>{{ 'leadQuality.config.block' | translate }}</th>
                  <th class="weight-col">{{ 'leadQuality.config.weight' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (criterion of config.scoreCriteria; track criterion.key) {
                  <tr>
                    <td class="fw-semibold">{{ criterionLabel(criterion) | translate }}</td>
                    <td>
                      <span class="badge bg-soft-secondary text-muted">
                        {{ blockLabel(criterion.block) | translate }}
                      </span>
                    </td>
                    <td>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="0"
                        max="100"
                        [(ngModel)]="criterion.weight"
                        [attr.aria-label]="criterionLabel(criterion) | translate"
                      />
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!--
            Qualification thresholds and the score's colour bands, SIDE BY SIDE on purpose
            (E3 RF-38, plan §11): they are two independent pairs of numbers on the same
            0–100 axis, and shown apart an administrator adjusts one expecting the other
            to follow. Since E3 the thresholds only produce the RECOMMENDATION — the
            qualification itself is decided by a person on the deal (RF-01).
          -->
          <h4 class="header-title mt-4">{{ 'leadQuality.config.qualification' | translate }}</h4>
          <p class="text-muted font-13">{{ 'leadQuality.config.scoreBandsHelp' | translate }}</p>
          <div class="row">
            <div class="col-md-3">
              <div class="mb-3">
                <label class="form-label" for="mqlMin">{{ 'leadQuality.config.mqlMin' | translate }}</label>
                <input id="mqlMin" type="number" class="form-control" min="0" max="100" [(ngModel)]="config.qualification.mqlMin" />
                <span class="help-block"><small class="text-muted">{{ 'leadQuality.config.mqlMinHelp' | translate }}</small></span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="mb-3">
                <label class="form-label" for="sqlMin">{{ 'leadQuality.config.sqlMin' | translate }}</label>
                <input id="sqlMin" type="number" class="form-control" min="0" max="100" [(ngModel)]="config.qualification.sqlMin" />
                <span class="help-block"><small class="text-muted">{{ 'leadQuality.config.sqlMinHelp' | translate }}</small></span>
              </div>
            </div>
            @if (config.scoreBands) {
              <div class="col-md-3">
                <div class="mb-3">
                  <label class="form-label" for="moderateMin">{{ 'leadQuality.config.moderateMin' | translate }}</label>
                  <input id="moderateMin" type="number" class="form-control" min="0" max="100" [(ngModel)]="config.scoreBands.moderateMin" />
                  <span class="help-block"><small class="text-muted">{{ 'leadQuality.config.scoreBands' | translate }}</small></span>
                </div>
              </div>
              <div class="col-md-3">
                <div class="mb-3">
                  <label class="form-label" for="strongMin">{{ 'leadQuality.config.strongMin' | translate }}</label>
                  <input id="strongMin" type="number" class="form-control" min="0" max="100" [(ngModel)]="config.scoreBands.strongMin" />
                  <span class="help-block"><small class="text-muted">{{ 'leadQuality.config.scoreBands' | translate }}</small></span>
                </div>
              </div>
            }
            <!--
              The stagnation threshold is deliberately NOT editable here: spec Q4 keeps it
              fixed in the interface, and the board derives its own cutoff from the
              platform constant without reading the company config. An input that accepted
              a value the board then ignored would be worse than no input at all.
            -->
          </div>

          <!--
            The trigger promotes a lead to sales-qualified regardless of score. Read-only
            here: adding trigger kinds is a product decision, not a per-company one.
          -->
          @if (config.qualification.sqlTriggers?.length) {
            <div class="alert alert-secondary font-13" role="alert">
              <i class="ri-flashlight-line me-1"></i>
              {{ 'leadQuality.config.triggerHint' | translate }}
            </div>
          }

          <!-- Relationship health -->
          <h4 class="header-title mt-4">{{ 'leadQuality.config.healthCriteria' | translate }}</h4>
          <div class="table-responsive">
            <table class="table table-centered table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th>{{ 'leadQuality.config.criterion' | translate }}</th>
                  <th class="weight-col">{{ 'leadQuality.config.weight' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (criterion of config.healthCriteria; track criterion.key) {
                  <tr>
                    <td>
                      <span class="fw-semibold">{{ criterionLabel(criterion) | translate }}</span>
                      @if (isCopilotSourced(criterion)) {
                        <!--
                          Reserved for the assistance layer and inert at weight 0. Weighting
                          it reintroduces a cross-database dependency (spec Q6).
                        -->
                        <br />
                        <small class="text-warning">
                          <i class="ri-alert-line me-1"></i>
                          {{ 'leadQuality.config.copilotSourcedHint' | translate }}
                        </small>
                      }
                    </td>
                    <td>
                      <input
                        type="number"
                        class="form-control form-control-sm"
                        min="0"
                        max="100"
                        [(ngModel)]="criterion.weight"
                        [attr.aria-label]="criterionLabel(criterion) | translate"
                      />
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <h4 class="header-title mt-4">{{ 'leadQuality.config.healthBands' | translate }}</h4>
          <p class="text-muted font-13">{{ 'leadQuality.config.healthBandsHelp' | translate }}</p>
          <div class="row">
            @for (band of config.healthBands; track band.band) {
              <div class="col-md-3">
                <div class="mb-3">
                  <label class="form-label">{{ bandLabel(band.band) | translate }}</label>
                  <div class="input-group input-group-sm">
                    <input
                      type="number"
                      class="form-control"
                      min="0"
                      max="100"
                      [(ngModel)]="band.min"
                      [attr.aria-label]="'leadQuality.config.bandMin' | translate"
                    />
                    <span class="input-group-text">—</span>
                    <input
                      type="number"
                      class="form-control"
                      min="0"
                      max="100"
                      [(ngModel)]="band.max"
                      [attr.aria-label]="'leadQuality.config.bandMax' | translate"
                    />
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Connection cadence (E3 RF-17): attempts without reaching the customer before a deal reads as exhausted. -->
          <h4 class="header-title mt-4">{{ 'leadQuality.config.cadence' | translate }}</h4>
          <div class="row">
            <div class="col-md-3">
              <div class="mb-3">
                <label class="form-label" for="cadenceMaxAttempts">{{ 'leadQuality.config.cadenceMaxAttempts' | translate }}</label>
                <input id="cadenceMaxAttempts" type="number" class="form-control" min="1" step="1" [(ngModel)]="config.cadenceMaxAttempts" />
                <span class="help-block"><small class="text-muted">{{ 'leadQuality.config.cadenceHelp' | translate }}</small></span>
              </div>
            </div>
          </div>

          <!--
            Ideal customer profile (E3 RF-28…RF-31): stated over the company's own segment
            and size lists (E2 RF-09). A ticked value is inside the profile; an empty list
            constrains nothing, so an unconfigured profile marks nobody out.
          -->
          <h4 class="header-title mt-4">{{ 'leadQuality.config.icp' | translate }}</h4>
          <p class="text-muted font-13">{{ 'leadQuality.config.icpHelp' | translate }} {{ 'leadQuality.config.icpEmptyHint' | translate }}</p>
          <div class="row">
            <div class="col-md-6">
              <label class="form-label">{{ 'leadQuality.config.icpSegments' | translate }}</label>
              @for (segment of icpOptions('segments'); track segment) {
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" [id]="'icp-segment-' + $index" [checked]="isInIcp('segments', segment)" (change)="toggleIcp('segments', segment)" />
                  <label class="form-check-label" [for]="'icp-segment-' + $index">
                    {{ segment }}
                    @if (isOrphanIcp('segments', segment)) {
                      <span class="badge bg-soft-warning text-warning ms-1 font-11">{{ 'leadQuality.config.icpOrphan' | translate }}</span>
                    }
                  </label>
                </div>
              }
            </div>
            <div class="col-md-6">
              <label class="form-label">{{ 'leadQuality.config.icpEmployeeBands' | translate }}</label>
              @for (band of icpOptions('employeeBands'); track band) {
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" [id]="'icp-band-' + $index" [checked]="isInIcp('employeeBands', band)" (change)="toggleIcp('employeeBands', band)" />
                  <label class="form-check-label" [for]="'icp-band-' + $index">
                    {{ band }}
                    @if (isOrphanIcp('employeeBands', band)) {
                      <span class="badge bg-soft-warning text-warning ms-1 font-11">{{ 'leadQuality.config.icpOrphan' | translate }}</span>
                    }
                  </label>
                </div>
              }
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-2">
            <button class="btn btn-light" (click)="restoreDefault()" [disabled]="saving">
              {{ 'leadQuality.config.discard' | translate }}
            </button>
            <button class="btn btn-primary" (click)="save()" [disabled]="saving">
              {{ 'leadQuality.config.save' | translate }}
            </button>
          </div>
        }
      </div>
    </div>
  </div>
</div>
